import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Printer, XCircle, Pencil } from "lucide-react";
import { formatKWD } from "@/lib/format";
import api, { formatApiError } from "@/lib/api";

const PAY_LABEL = { cash: "نقداً", knet: "كي نت", online: "أونلاين" };
const M_LABELS = { length: "الطول", shoulder: "الكتف", chest: "الصدر", sleeve: "طول الكم", hip: "محيط الورك" };

export default function AdminInvoices() {
  const nav = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(null); // full invoice
  const [editing, setEditing] = useState(null); // {items:[], delivery_fee, reason}
  const [cancelReason, setCancelReason] = useState("");
  const [cancelErr, setCancelErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openInvoice = async (id) => {
    try { const { data } = await api.get(`/admin/invoices/${id}/full`); setOpen(data); setEditing(null); setCancelReason(""); setCancelErr(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/report-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "rnf-invoices.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("تعذّر التصدير"); }
  };

  const startEdit = () => setEditing({
    items: open.items.map((it) => ({ name_ar: it.name_ar, sku: it.sku || "", quantity: it.quantity || 1, unit_price: it.unit_price })),
    delivery_fee: open.delivery_fee || 0, reason: "",
  });

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/invoices/${open.id}/edit`, editing);
      toast.success("تم تعديل الفاتورة وإنشاء نسخة مراجعة");
      await openInvoice(open.id); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (!cancelReason.trim()) { setCancelErr("يجب إدخال سبب الإلغاء — لم تتغيّر حالة الفاتورة"); toast.error("أدخلي سبب الإلغاء"); return; }
    setCancelErr("");
    setBusy(true);
    try {
      await api.put(`/admin/invoices/${open.id}/cancel`, { reason: cancelReason });
      toast.success("تم إلغاء الفاتورة");
      setOpen(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl">المبيعات والفواتير</h1>
        <button onClick={exportCsv} data-testid="invoices-export-csv" className="bg-[#141110] text-white px-4 py-2 text-sm hover:bg-black transition-colors">تصدير CSV</button>
      </div>

      {/* Mobile cards */}
      <div className="grid gap-3 md:hidden">
        {invoices.filter((i) => i.invoice_no).map((inv) => (
          <div key={inv.id} className={`bg-white border border-border p-4 ${inv.status === "cancelled" ? "opacity-60" : ""}`} data-testid={`invoice-card-${inv.invoice_no}`}>
            <div className="flex justify-between items-start">
              <span className="font-medium">#{inv.invoice_no}{inv.revision ? ` (${inv.revision})` : ""}</span>
              <span className={inv.status === "cancelled" ? "text-destructive text-xs" : "text-green-700 text-xs"}>{inv.status === "cancelled" ? "ملغاة" : "مكتملة"}</span>
            </div>
            <div className="text-xs text-text-muted mt-1">{new Date(inv.created_at).toLocaleDateString("ar-KW")} · {inv.source === "STORE" ? "المتجر" : "الموقع"} · {inv.seller_name || (inv.source === "WEB" ? "الموقع (عميلة)" : "—")}</div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm">{PAY_LABEL[inv.payment_method] || inv.payment_method} · {formatKWD(inv.total)}</span>
              <button onClick={() => openInvoice(inv.id)} className="text-accent underline text-xs" data-testid={`invoice-view-m-${inv.invoice_no}`}>تفاصيل</button>
            </div>
          </div>
        ))}
        {invoices.filter((i) => i.invoice_no).length === 0 && <p className="p-6 text-center text-text-muted text-sm">لا توجد فواتير بعد</p>}
      </div>

      <div className="bg-white border border-border overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-surface text-text-muted text-xs">
            <tr>
              <th className="text-start p-3">رقم</th><th className="text-start p-3">التاريخ</th>
              <th className="text-start p-3">المصدر</th><th className="text-start p-3">البائعة</th>
              <th className="text-start p-3">الدفع</th><th className="text-start p-3">الحالة</th>
              <th className="text-start p-3">الإجمالي</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.filter((i) => i.invoice_no).map((inv) => (
              <tr key={inv.id} className={`border-t border-border ${inv.status === "cancelled" ? "opacity-50" : ""}`} data-testid={`invoice-row-${inv.invoice_no}`}>
                <td className="p-3">#{inv.invoice_no}{inv.revision ? ` (${inv.revision})` : ""}</td>
                <td className="p-3 text-text-muted">{new Date(inv.created_at).toLocaleDateString("ar-KW")}</td>
                <td className="p-3">{inv.source === "STORE" ? "المتجر" : "الموقع"}</td>
                <td className="p-3">{inv.seller_name || (inv.source === "WEB" ? "الموقع (عميلة)" : "—")}</td>
                <td className="p-3">{PAY_LABEL[inv.payment_method] || inv.payment_method}</td>
                <td className="p-3">{inv.status === "cancelled" ? <span className="text-destructive">ملغاة</span> : "مكتملة"}</td>
                <td className="p-3">{formatKWD(inv.total)}</td>
                <td className="p-3 text-end">
                  <button onClick={() => openInvoice(inv.id)} className="text-accent underline text-xs" data-testid={`invoice-view-${inv.invoice_no}`}>تفاصيل</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {invoices.filter((i) => i.invoice_no).length === 0 && <p className="p-8 text-center text-text-muted text-sm">لا توجد فواتير بعد</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white w-full max-w-md p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="invoice-detail-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-2xl">فاتورة #{open.invoice_no}{open.revision ? ` · ${open.revision}` : ""}</h3>
              {open.status !== "cancelled" && <span className="text-xs text-green-700">مكتملة</span>}
              {open.status === "cancelled" && <span className="text-xs text-destructive">ملغاة</span>}
            </div>
            <p className="text-xs text-text-muted mb-4">{new Date(open.created_at).toLocaleString("ar-KW")} · {open.seller_name || "الموقع"} · {open.customer_name || "—"}</p>

            {!editing ? (
              <>
                {open.items.map((it, i) => (
                  <div key={i} className="border-b border-border py-2 text-sm">
                    <div className="flex justify-between"><span>{it.name_ar} {it.size ? `· ${it.size}/${it.color_ar || it.color}` : ""} ×{it.quantity || 1}</span><span>{formatKWD(it.line_total)}</span></div>
                    {it.measurements && <div className="text-[11px] text-text-muted mt-0.5">{Object.entries(it.measurements).map(([k, v]) => `${M_LABELS[k] || k} ${v}`).join(" · ")} سم</div>}
                    {it.notes && <div className="text-[11px] text-text-muted">ملاحظات: {it.notes}</div>}
                  </div>
                ))}
                <div className="flex justify-between text-sm mt-2"><span>التوصيل / الشحن</span><span>{formatKWD(open.delivery_fee || 0)}</span></div>
                <div className="flex justify-between mt-1 font-medium"><span>الإجمالي</span><span>{formatKWD(open.total)}</span></div>

                {open.revisions?.length > 0 && (
                  <div className="mt-4 bg-surface p-3 text-xs" data-testid="invoice-revisions">
                    <div className="font-medium mb-1">سجل المراجعات (الأصل محفوظ):</div>
                    {open.revisions.map((r, i) => (
                      <div key={i} className="text-text-muted">{r.rev}: إجمالي سابق {formatKWD(r.total)} — {r.reason || "بدون سبب"} · {new Date(r.at).toLocaleString("ar-KW")}</div>
                    ))}
                  </div>
                )}
                {open.status === "cancelled" && (
                  <div className="mt-3 text-xs text-destructive" data-testid="invoice-cancel-info">سبب الإلغاء: {open.cancel_reason} · {new Date(open.cancelled_at).toLocaleString("ar-KW")}</div>
                )}

                <div className="flex flex-wrap gap-2 mt-5">
                  <button onClick={() => nav(`/pos/receipt/${open.id}?from=admin`)} data-testid="invoice-print" className="flex items-center gap-2 bg-[#141110] text-white px-5 min-h-[48px] text-base font-bold rounded-md hover:bg-black"><Printer className="w-5 h-5" /> إعادة طباعة الفاتورة</button>
                  {open.status !== "cancelled" && <button onClick={startEdit} data-testid="invoice-edit-btn" className="flex items-center gap-1 border border-border px-3 py-2 text-xs"><Pencil className="w-3.5 h-3.5" /> تعديل</button>}
                  {open.status !== "cancelled" && (
                    <div className="w-full mt-2">
                      <input value={cancelReason} onChange={(e) => { setCancelReason(e.target.value); setCancelErr(""); }} placeholder="سبب الإلغاء" data-testid="invoice-cancel-reason" className="w-full border border-border px-3 py-2 text-base mb-2" />
                      {cancelErr && <p className="text-xs text-destructive mb-2" data-testid="invoice-cancel-error">{cancelErr}</p>}
                      <button type="button" onClick={doCancel} disabled={busy} data-testid="invoice-cancel-btn" className="flex items-center gap-1 text-destructive border border-destructive px-3 py-2 text-xs"><XCircle className="w-3.5 h-3.5" /> إلغاء الفاتورة</button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="space-y-3" data-testid="invoice-edit-form">
                {editing.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[1fr_60px_80px] gap-2 items-end">
                    <div><label className="text-[10px] text-text-muted">الصنف</label><input value={it.name_ar} onChange={(e) => { const c = [...editing.items]; c[i].name_ar = e.target.value; setEditing({ ...editing, items: c }); }} className="w-full border border-border px-2 py-1.5 text-xs" /></div>
                    <div><label className="text-[10px] text-text-muted">كمية</label><input type="number" value={it.quantity} onChange={(e) => { const c = [...editing.items]; c[i].quantity = +e.target.value; setEditing({ ...editing, items: c }); }} className="w-full border border-border px-2 py-1.5 text-xs" /></div>
                    <div><label className="text-[10px] text-text-muted">سعر</label><input type="number" step="0.001" value={it.unit_price} onChange={(e) => { const c = [...editing.items]; c[i].unit_price = +e.target.value; setEditing({ ...editing, items: c }); }} className="w-full border border-border px-2 py-1.5 text-xs" /></div>
                  </div>
                ))}
                <div><label className="text-[10px] text-text-muted">رسوم التوصيل</label><input type="number" step="0.001" value={editing.delivery_fee} onChange={(e) => setEditing({ ...editing, delivery_fee: +e.target.value })} className="w-full border border-border px-2 py-1.5 text-xs" /></div>
                <input value={editing.reason} onChange={(e) => setEditing({ ...editing, reason: e.target.value })} placeholder="سبب التعديل" data-testid="edit-reason" className="w-full border border-border px-3 py-2 text-sm" />
                <div className="flex gap-2">
                  <button onClick={saveEdit} disabled={busy} data-testid="save-edit-btn" className="flex-1 bg-primary text-primary-foreground py-2.5 text-sm">حفظ نسخة مراجعة</button>
                  <button onClick={() => setEditing(null)} className="border border-border px-4 text-sm">إلغاء</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
